/**
 * Thin client for `POST /v1/broker`.
 *
 * Every call sends the Cognito id-token in the `Authorization` header. Errors
 * are surfaced as a typed `BrokerApiError` with the BAL's documented codes
 * so the UI can render contextual messages (mfa_required, in_progress, …).
 *
 * The API base URL is read from `import.meta.env.VITE_API_BASE` at build time —
 * a single change of `vite build`'s env switches the SPA between dev / staging /
 * prod without code edits.
 */
export type BalAction =
  | 'link' | 'unlink' | 'accounts' | 'positions' | 'balances'
  | 'placeOrder' | 'modifyOrder' | 'cancelOrder' | 'ping';

export type BalErrorCode =
  | 'unauthenticated' | 'mfa_required' | 'invalid_action' | 'missing_brokerId'
  | 'unknown_broker' | 'in_progress' | 'duplicate_idempotency_key_with_different_body'
  | 'validation_failed' | 'auth_failed' | 'insufficient_funds' | 'symbol_suspended'
  | 'symbol_not_found' | 'order_already_terminal' | 'rate_limited' | 'upstream_unavailable'
  | 'internal_error' | 'invalid_json' | 'payload_too_large' | 'method_not_allowed'
  | 'lot_below_min' | 'bond_suspended' | 'etf_suspended'
  | 'unknown';

export class BrokerApiError extends Error {
  constructor(
    public readonly code: BalErrorCode,
    message: string,
    public readonly status: number,
    public readonly retryable = false,
  ) {
    super(message);
    this.name = 'BrokerApiError';
  }
}

export interface BrokerClientConfig {
  baseUrl: string;          // e.g. https://nigastock.ng/api  (CloudFront /api/* behaviour proxies to API Gateway)
  getIdToken: () => Promise<string | undefined>;
  fetchImpl?: typeof fetch; // overridable for tests
}

export class BrokerClient {
  constructor(private readonly cfg: BrokerClientConfig) {}

  /** Generic action dispatcher — returns the parsed JSON body on 2xx. */
  async call<T = unknown>(action: BalAction, brokerId: string, payload: Record<string, unknown> = {}): Promise<T> {
    const f = this.cfg.fetchImpl ?? fetch;
    const token = await this.cfg.getIdToken();
    if (!token) throw new BrokerApiError('unauthenticated', 'no id-token', 401);

    const res = await f(`${this.cfg.baseUrl.replace(/\/$/, '')}/broker`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ action, brokerId, payload }),
    });

    const text = await res.text();
    let body: any = undefined;
    if (text.length > 0) {
      try { body = JSON.parse(text); } catch { /* leave undefined */ }
    }

    if (res.ok) return body as T;

    const code: BalErrorCode = (body && typeof body.error === 'string') ? body.error as BalErrorCode : 'unknown';
    const retryable = !!(body && body.retryable);
    throw new BrokerApiError(code, code.replace(/_/g, ' '), res.status, retryable);
  }

  // Convenience wrappers — keep these intentionally thin.
  accounts(brokerId: string) { return this.call<{ accounts: any[] }>('accounts', brokerId); }
  positions(brokerId: string, accountId: string) { return this.call<{ positions: any[] }>('positions', brokerId, { accountId }); }
  balances(brokerId: string, accountId: string) { return this.call<{ balances: any[] }>('balances', brokerId, { accountId }); }

  /**
   * Place an order — DEMO MODE ONLY
   * This is a safety check to prevent real trades if someone tries to use BrokerClient directly.
   * All trading in this application is demo-only and should use MockBrokerClient.
   */
  placeOrder(brokerId: string, payload: Record<string, unknown>) {
    // Safety check: if not in demo mode, block the order
    const isDemoMode = ((import.meta as any).env?.VITE_DEMO_MODE ?? '').toLowerCase() === 'true';
    if (!isDemoMode) {
      console.error('🚨 DEMO MODE DISABLED: Order blocked. This application is in DEMO mode only.');
      throw new BrokerApiError(
        'invalid_action',
        'Demo mode is not properly enforced. Trading is blocked for safety.',
        400
      );
    }
    return this.call<any>('placeOrder', brokerId, payload);
  }

  ping(brokerId: string) { return this.call<{ ok: boolean }>('ping', brokerId); }
}
